import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Empty, Segmented, Select, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { ArrowDownRight, ArrowRight, ArrowUpRight, CalendarCheck, ChevronRight, Database, Phone, Siren, Star, Users } from 'lucide-react';
import Layout from '../../Layout';
import {
  APPOINTMENT_STATUS, COMPLAINT_CATEGORY, MANAGER, SEVERITY, SYSTEM_AGENT, agentsOf, assigneeOf, callCenterApi, complaintMetaOf, customerById,
  customerByPhone, fmtDuration, isDoNotCall, rangeBounds, taskText, useCallCenter,
  type Appointment, type Callback, type RangePreset,
} from '../CustomerDemo/callCenterStore';
import { meName, softphone } from '../CustomerDemo/softphone';
import Softphone from '../CustomerDemo/components/Softphone';
import { AgentStatusDropdown } from '../CustomerDemo/components/CallWidgets';
import { TaskDoneButton } from '../CustomerDemo/components/TaskWidgets';
import { DAILY_CALL_TARGET, TASK_TYPE, agentStats, dashboardData, taskTypeOf, type TaskType } from './cskhStats';
import { PageHeader, detailUrl, dueText } from './shared';
import { clearCskhDemo, hasCskhDemo, loadCskhDemo, useCskhDemoAutoload } from './demoData';

const TYPE_TONE: Record<TaskType, string> = {
  medical: 'text-red-600 bg-red-50', complaint: 'text-rose-600 bg-rose-50', survey: 'text-amber-700 bg-amber-50',
  aftercare: 'text-teal-700 bg-teal-50', callback: 'text-blue-600 bg-blue-50',
};
const RANGES: { value: RangePreset; label: string; days: number }[] = [
  { value: 'today', label: 'Hôm nay', days: 1 }, { value: '7d', label: '7 ngày', days: 7 }, { value: '30d', label: '30 ngày', days: 30 },
];

type FocusTab = 'todo' | 'complaints' | 'surveys' | 'appointments';
const LIMIT = 6;
const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];

/** Chênh lệch so với kỳ trước: xanh khi tốt lên (goodWhenUp quyết định chiều tốt) */
function Delta({ diff, suffix, goodWhenUp = true, digits = 0 }: { diff: number | null; suffix: string; goodWhenUp?: boolean; digits?: number }) {
  if (diff === null) return null;
  if (Math.abs(diff) < 10 ** -digits / 2) return <span className="text-gray-400">= {suffix}</span>;
  const up = diff > 0;
  const good = up === goodWhenUp;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 ${good ? 'text-emerald-600' : 'text-rose-600'}`}>
      <Icon size={12} />{Math.abs(diff).toFixed(digits)} {suffix}
    </span>
  );
}

function Metric({ label, value, sub, delta, alert, onClick }: {
  label: string; value: React.ReactNode; sub: React.ReactNode; delta?: React.ReactNode; alert?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className="group text-left px-5 py-4 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition cursor-pointer min-w-0">
      <div className="text-xs text-gray-500 flex items-center justify-between gap-2">
        {label}
        <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 text-[#3e91ab] transition" />
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-2xl font-bold leading-tight ${alert ? 'text-rose-600' : 'text-gray-900 dark:text-gray-100'}`}>{value}</span>
        {delta && <span className="text-[11px]">{delta}</span>}
      </div>
      <div className="text-xs text-gray-400 mt-0.5 truncate">{sub}</div>
    </button>
  );
}

/** Dòng trong "Cần xử lý": bấm vùng trái mở hồ sơ khách, bên phải là thao tác nhanh */
function Row({ tag, tagCls, title, desc, due, dueCls = 'text-gray-400', onOpen, actions }: {
  tag: string; tagCls: string; title: React.ReactNode; desc: React.ReactNode; due: React.ReactNode; dueCls?: string; onOpen: () => void; actions: React.ReactNode;
}) {
  return (
    <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition">
      <button type="button" onClick={onOpen} className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md shrink-0 w-[92px] text-center truncate ${tagCls}`}>{tag}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{title}</div>
          <div className="text-xs text-gray-500 truncate">{desc}</div>
        </div>
        <span className={`text-xs shrink-0 ${dueCls}`}>{due}</span>
      </button>
      <div className="flex items-center gap-1.5 shrink-0 justify-end">{actions}</div>
    </div>
  );
}

const Card = ({ className = '', children }: { className?: string; children: React.ReactNode }) => (
  <div className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm min-w-0 ${className}`}>{children}</div>
);

function CallButton({ phone, callbackId, label }: { phone: string; callbackId?: string; label?: string }) {
  const s = useCallCenter();
  const blocked = !phone || isDoNotCall(s, phone);
  return (
    <Tooltip title={blocked ? 'Khách đang chặn gọi' : label ? undefined : 'Gọi ngay'}>
      <Button size="small" type="primary" ghost icon={<Phone size={12} />} disabled={blocked} onClick={() => softphone.dial(phone, { callbackId })}>{label}</Button>
    </Tooltip>
  );
}

const fmtWhen = (t: number, now: number) => dayjs(t).format(dayjs(t).isSame(now, 'day') ? 'HH:mm [hôm nay]' : 'HH:mm [ngày mai]');

/** Tổng quan CSKH theo người xem: chỉ số có so sánh, cảnh báo khẩn, việc cần làm có thao tác nhanh, hiệu suất nhân viên */
export default function CskhDashboardPage() {
  useCskhDemoAutoload();
  const s = useCallCenter();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const range = (params.get('range') as RangePreset) || 'today';
  const who = params.get('who');
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  // Hạn "trễ 5 phút / còn 1 giờ" tự cập nhật mỗi phút
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  useEffect(() => setNow(Date.now()), [s]);

  const me = meName();
  const d = dashboardData(s, who, now);
  const agents = agentStats(s, rangeBounds(range, null, null, now)!, now).filter(a => a.agent !== SYSTEM_AGENT);
  const days = RANGES.find(r => r.value === range)!.days;
  const target = DAILY_CALL_TARGET * days;
  const whoOptions = useMemo(() => {
    const names = Array.from(new Set([...agentsOf(s), ...s.callbacks.filter(cb => !cb.doneAt).map(cb => assigneeOf(s, cb))]))
      .filter(n => n && n !== SYSTEM_AGENT && n !== me);
    return [
      { value: '', label: 'Cả nhóm' },
      { value: me, label: `Của tôi (${me})` },
      ...names.sort((a, b) => (a === MANAGER ? 1 : b === MANAGER ? -1 : a.localeCompare(b))).map(n => ({ value: n, label: n })),
    ];
  }, [s, me]);

  const [focus, setFocus] = useState<FocusTab>(() => (d.todo.length ? 'todo' : d.complaints.length ? 'complaints' : d.surveysDue.length ? 'surveys' : 'appointments'));
  const scopeQuery = who ? `&assignee=${encodeURIComponent(who)}` : '';
  const tabs: { value: FocusTab; label: string; count: number; hot: boolean; to: string }[] = [
    { value: 'todo', label: 'Việc cần làm', count: d.todo.length, hot: d.overdue.length > 0, to: `/cskh-tasks?tab=${d.overdue.length ? 'overdue' : 'today'}${scopeQuery}` },
    { value: 'complaints', label: 'Khiếu nại', count: d.complaints.length, hot: d.complaintsOverdue > 0, to: '/cskh-complaints' },
    { value: 'surveys', label: 'Cần khảo sát', count: d.surveysDue.length, hot: false, to: '/cskh-surveys' },
    { value: 'appointments', label: 'Lịch hẹn 24h', count: d.appointments.length, hot: d.appointments.some(a => a.status === 'new'), to: '' },
  ];
  const current = tabs.find(t => t.value === focus)!;

  const taskRow = (cb: Callback, tag?: { text: string; cls: string }) => {
    const c = customerByPhone(s, cb.phone);
    const t = taskTypeOf(cb);
    const late = cb.dueAt < now;
    return (
      <Row key={cb.id} tag={tag?.text || TASK_TYPE[t]} tagCls={tag?.cls || TYPE_TONE[t]} title={c?.name || cb.phone}
        desc={<>{who ? '' : <span className="text-gray-400">{assigneeOf(s, cb)} · </span>}{taskText(cb) || 'Gọi lại cho khách'}</>}
        due={dueText(cb.dueAt, now)} dueCls={late ? 'text-rose-600 font-medium' : 'text-gray-400'}
        onOpen={() => navigate(detailUrl(c, cb.phone))}
        actions={<><CallButton phone={cb.phone} callbackId={cb.id} /><TaskDoneButton cb={cb} /></>} />
    );
  };

  const apptRow = (a: Appointment) => {
    const c = customerById(s, a.customerId);
    const isNew = a.status === 'new';
    return (
      <Row key={a.id} tag={isNew ? 'Chưa xác nhận' : APPOINTMENT_STATUS[a.status].label} tagCls={isNew ? 'text-amber-700 bg-amber-50' : 'text-purple-600 bg-purple-50'}
        title={c?.name || 'Khách'} desc={`${a.service} · ${a.practitioner || a.branch}${a.note ? ` · ${a.note}` : ''}`}
        due={fmtWhen(a.at, now)} dueCls={isNew && a.at - now < 3 * 3600000 ? 'text-amber-600 font-medium' : 'text-gray-500'}
        onOpen={() => navigate(detailUrl(c, c?.phones[0]))}
        actions={<>
          <CallButton phone={c?.phones[0] || ''} label="Gọi nhắc" />
          {isNew && (
            <Button size="small" icon={<CalendarCheck size={12} />}
              onClick={() => { if (callCenterApi.setAppointmentStatus(a.id, 'confirmed', me)) message.success(`Đã xác nhận lịch của ${c?.name || 'khách'}`); }}>Xác nhận</Button>
          )}
        </>} />
    );
  };

  const rows: React.ReactNode[] = focus === 'todo'
    ? d.todo.slice(0, LIMIT).map(cb => taskRow(cb))
    : focus === 'complaints'
      ? d.complaints.slice(0, LIMIT).map(cb => {
        const m = complaintMetaOf(cb)!;
        return taskRow(cb, { text: `${SEVERITY[m.severity].label} · ${COMPLAINT_CATEGORY[m.category]}`, cls: SEVERITY[m.severity].badge });
      })
      : focus === 'surveys'
        ? d.surveysDue.slice(0, LIMIT).map(p => {
          const c = customerById(s, p.appointment.customerId);
          return (
            <Row key={p.appointment.id} tag={p.overdue ? 'Quá hạn' : 'Đến hạn'} tagCls={p.overdue ? 'text-rose-600 bg-rose-50' : 'text-amber-700 bg-amber-50'}
              title={c?.name || 'Khách'} desc={`${p.appointment.service} · làm ngày ${dayjs(p.appointment.at).format('DD/MM')}${p.attempts ? ` · đã gọi ${p.attempts} lần không được` : ''}`}
              due={dayjs(p.dueAt).format('DD/MM')} onOpen={() => navigate(detailUrl(c, c?.phones[0]))}
              actions={<>
                <CallButton phone={c?.phones[0] || ''} />
                <Button size="small" icon={<Star size={12} />} onClick={() => navigate(detailUrl(c, c?.phones[0], `&survey=${p.appointment.id}`))}>Khảo sát</Button>
              </>} />
          );
        })
        : d.appointments.slice(0, LIMIT).map(apptRow);

  const urgentShown = d.urgent.slice(0, 3);

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-5">
        <PageHeader title={who === me ? 'Việc của tôi hôm nay' : who ? `Tổng quan · ${who}` : 'Tổng quan CSKH'}
          desc={`${WEEKDAYS[dayjs(now).day()]}, ${dayjs(now).format('DD/MM/YYYY · HH:mm')} — tự cập nhật mỗi phút`}
          extra={<>
            <Select className="w-[220px]" value={who || ''} options={whoOptions} suffixIcon={<Users size={14} />} showSearch optionFilterProp="label"
              onChange={v => go({ who: v || null })} />
            <Dropdown trigger={['click']} menu={{
              items: [
                { key: 'load', label: hasCskhDemo(s) ? 'Làm mới dữ liệu demo' : 'Nạp dữ liệu demo' },
                { key: 'remove', label: 'Gỡ dữ liệu demo', danger: true, disabled: !hasCskhDemo(s) },
              ],
              onClick: ({ key }) => {
                if (key === 'load') loadCskhDemo(); else clearCskhDemo();
                message.success(key === 'load' ? 'Đã nạp dữ liệu demo CSKH' : 'Đã gỡ dữ liệu demo CSKH');
              },
            }}>
              <Tooltip title="Dữ liệu demo"><Button aria-label="Dữ liệu demo" icon={<Database size={14} />} /></Tooltip>
            </Dropdown>
            <AgentStatusDropdown />
          </>} />

        {/* Cảnh báo khẩn — chỉ hiện khi có */}
        {d.urgent.length > 0 && (
          <div className="rounded-2xl border border-rose-200 dark:border-rose-900 bg-rose-50/70 dark:bg-rose-950/20 px-5 py-3 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-semibold text-sm shrink-0">
              <Siren size={16} /> {d.urgent.length} việc khẩn cần xử lý ngay
            </div>
            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
              {urgentShown.map(cb => {
                const c = customerByPhone(s, cb.phone);
                const m = complaintMetaOf(cb);
                return (
                  <div key={cb.id} className="flex items-center gap-2 bg-white dark:bg-gray-900 border border-rose-100 dark:border-rose-900 rounded-xl pl-3 pr-1 py-1 min-w-0">
                    <button type="button" onClick={() => navigate(detailUrl(c, cb.phone))} className="text-left min-w-0 cursor-pointer">
                      <div className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">{c?.name || cb.phone}</div>
                      <div className="text-[11px] text-rose-600 truncate max-w-[200px]">
                        {m ? `Khiếu nại ${SEVERITY[m.severity].label.toLowerCase()}` : 'Bất thường sau DV'} · {dueText(cb.dueAt, now)}
                      </div>
                    </button>
                    <CallButton phone={cb.phone} callbackId={cb.id} />
                  </div>
                );
              })}
              {d.urgent.length > urgentShown.length && (
                <button type="button" onClick={() => navigate('/cskh-tasks?tab=overdue')} className="text-xs text-rose-700 hover:underline cursor-pointer self-center">
                  +{d.urgent.length - urgentShown.length} việc khác
                </button>
              )}
            </div>
          </div>
        )}

        {/* 1. Dải chỉ số */}
        <Card className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden !bg-gray-100 dark:!bg-gray-800">
          <Metric label="Cuộc gọi hôm nay" value={d.calls.today} onClick={() => navigate('/customer-demo?range=today')}
            delta={<Delta diff={d.calls.today - d.calls.yesterday} suffix="so với hôm qua" />}
            sub={`Nhỡ ${d.calls.missedRate}% · cùng giờ hôm qua ${d.calls.yesterday} cuộc`} />
          <Metric label="Việc cần làm hôm nay" value={d.todo.length} alert={d.overdue.length > 0} onClick={() => navigate(tabs[0].to)}
            sub={`${d.overdue.length} quá hạn · ${d.doneToday} đã xong hôm nay`} />
          <Metric label="Khiếu nại đang mở" value={d.complaints.length} alert={d.complaintsOverdue > 0} onClick={() => navigate('/cskh-complaints')}
            sub={d.complaintsOverdue ? `${d.complaintsOverdue} quá hạn SLA` : 'Tất cả trong hạn'} />
          <Metric label="Hài lòng 30 ngày" value={d.csat.csat === null ? '—' : `${d.csat.csat.toFixed(1)}★`} onClick={() => navigate('/cskh-surveys?tab=done')}
            delta={<Delta diff={d.csat.csat !== null && d.csat.csat !== undefined && d.csatPrev.csat !== null ? d.csat.csat - d.csatPrev.csat : null} suffix="★" digits={1} />}
            sub={d.csat.nps === null ? `${d.csat.done} khảo sát` : `NPS ${d.csat.nps} · ${d.csat.done} khảo sát`} />
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
          {/* 2. Cần xử lý */}
          <Card className="xl:col-span-2 flex flex-col">
            <div className="px-5 pt-4 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="font-semibold text-gray-900 dark:text-gray-100">Cần xử lý</div>
              <div className="overflow-x-auto">
                <Segmented size="small" value={focus} onChange={v => setFocus(v as FocusTab)}
                  options={tabs.map(t => ({
                    value: t.value,
                    label: <span className="px-1">{t.label}{t.count > 0 && <span className={`ml-1.5 text-[11px] font-semibold ${t.hot ? 'text-rose-600' : 'text-gray-400'}`}>{t.count}</span>}</span>,
                  }))} />
              </div>
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
              {rows.length ? rows : <Empty className="py-10" image={Empty.PRESENTED_IMAGE_SIMPLE} description={who === me ? 'Bạn đã xử lý hết việc 🎉' : 'Không có gì cần xử lý 🎉'} />}
            </div>
            {current.to && <button type="button" onClick={() => navigate(current.to)}
              className="border-t border-gray-100 dark:border-gray-800 px-5 py-2.5 text-xs text-[#3e91ab] hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer flex items-center justify-center gap-1 rounded-b-2xl">
              {current.count > LIMIT ? `Xem tất cả ${current.count}` : 'Mở trang chi tiết'} <ChevronRight size={13} />
            </button>}
          </Card>

          {/* 3. Nhân viên */}
          <Card className="flex flex-col">
            <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100">Nhân viên</div>
                <div className="text-[11px] text-gray-400">Mục tiêu {DAILY_CALL_TARGET} cuộc/người/ngày · bấm tên để xem việc</div>
              </div>
              <Segmented size="small" value={range} options={RANGES.map(r => ({ value: r.value, label: r.label }))} onChange={v => go({ range: v === 'today' ? null : String(v) })} />
            </div>
            <div className="border-t border-gray-100 dark:border-gray-800">
              {agents.length ? agents.slice(0, 8).map(a => {
                const pct = Math.min(100, Math.round((a.calls / target) * 100));
                const selected = who === a.agent;
                return (
                  <button key={a.agent} type="button" onClick={() => go({ who: selected ? null : a.agent })}
                    className={`w-full text-left px-5 py-3 border-b last:border-b-0 border-gray-100 dark:border-gray-800 cursor-pointer transition ${selected ? 'bg-[#3e91ab]/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium truncate ${selected ? 'text-[#3e91ab]' : 'text-gray-900 dark:text-gray-100'}`}>{a.agent}{a.agent === me ? ' (tôi)' : ''}</span>
                      {a.tasksOpen > 0
                        ? <span className="text-[11px] text-rose-600 font-medium shrink-0">{a.tasksOpen} việc quá hạn</span>
                        : <span className="text-[11px] text-gray-400 shrink-0">{a.tasksDone} việc xong</span>}
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-[#3e91ab]'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1 text-[11px] text-gray-500 flex justify-between gap-2">
                      <span className="truncate">{a.calls ? Math.round((a.answered / a.calls) * 100) : 0}% nghe máy{a.answered ? ` · TB ${fmtDuration(Math.round(a.talk / a.answered))}` : ''}</span>
                      <span className={`shrink-0 ${pct >= 100 ? 'text-emerald-600 font-medium' : ''}`}>{a.calls}/{target} cuộc</span>
                    </div>
                  </button>
                );
              }) : <Empty className="py-10" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hoạt động" />}
            </div>
          </Card>
        </div>
      </div>
      <Softphone />
    </Layout>
  );
}
